import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { useState } from 'react'

import { AnalysisResult } from '@/gen/controls/v1/analysis_result_pb'

import { type EventProperties, useTrackCallback } from '@/lib/analytics/events'

import { RISK_QUESTIONNAIRE_THREAD_LABELS } from '@/pages/control/labels'
import { EarlierEvaluation } from '@/pages/control/risk-questionnaire-thread-card/earlier-evaluation'
import type { StatusChange } from '@/pages/control/risk-questionnaire-thread-card/types'

import { Button } from '@/components/lemmala-design-system/button/button'
import { Collapsible } from '@/components/lemmala-design-system/collapsible/collapsible'
import { CollapsibleContent } from '@/components/lemmala-design-system/collapsible/collapsible-content'
import { CollapsibleTrigger } from '@/components/lemmala-design-system/collapsible/collapsible-trigger'

type EarlierEvaluationsProps = {
  evaluations: AnalysisResult[]
  statusChanges: Map<string, StatusChange>
  trackingProps: EventProperties
}

export const EarlierEvaluations = ({
  evaluations,
  statusChanges,
  trackingProps,
}: EarlierEvaluationsProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const trackGroupClick = useTrackCallback('control.view.activity.group-controls-click')

  const toggle = (open: boolean) => {
    setIsExpanded(open)
    trackGroupClick({ ...trackingProps, type: open ? 'expand' : 'collapse' })
  }

  return (
    <Collapsible open={isExpanded} onOpenChange={toggle} className='flex flex-col gap-2'>
      <CollapsibleTrigger asChild>
        <Button type='button' variant='link' className='h-auto gap-2 space-x-0 self-start p-0'>
          {isExpanded
            ? RISK_QUESTIONNAIRE_THREAD_LABELS.HIDE_EARLIER_EVALUATIONS
            : RISK_QUESTIONNAIRE_THREAD_LABELS.SHOW_EARLIER_EVALUATIONS(evaluations.length)}
          {isExpanded ? (
            <ChevronUpIcon className='size-4' aria-hidden />
          ) : (
            <ChevronDownIcon className='size-4' aria-hidden />
          )}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ol className='flex flex-col gap-3.5 border-l-2 border-main-border-default pl-4'>
          {evaluations.map((evaluation) => (
            <EarlierEvaluation
              key={evaluation.id}
              evaluation={evaluation}
              change={statusChanges.get(evaluation.id)}
            />
          ))}
        </ol>
      </CollapsibleContent>
    </Collapsible>
  )
}
